import { connectedGamepad, gamepadHasActivity } from "./gamepad";

export type InputDevice = "keyboard" | "gamepad" | "touch";

export const ON_SCREEN_CONTROL_QUERY = "(hover: none) and (pointer: coarse)";

let currentDevice: InputDevice = defaultInputDevice();
const listeners = new Set<(device: InputDevice) => void>();

export function prefersOnScreenControls(
  query: { matches: boolean } | null | undefined = matchOnScreenControlQuery()
): boolean {
  return Boolean(query?.matches);
}

export function defaultInputDevice(
  query: { matches: boolean } | null | undefined = matchOnScreenControlQuery()
): InputDevice {
  return prefersOnScreenControls(query) ? "touch" : "keyboard";
}

function matchOnScreenControlQuery(
  view: Window | undefined = globalThis.window
): { matches: boolean } | undefined {
  try {
    return view?.matchMedia?.(ON_SCREEN_CONTROL_QUERY);
  } catch {
    return undefined;
  }
}

export function getActiveInputDevice(): InputDevice {
  return currentDevice;
}

export function subscribeActiveInputDevice(listener: (device: InputDevice) => void): () => void {
  listeners.add(listener);
  listener(currentDevice);
  return () => {
    listeners.delete(listener);
  };
}

export function resolveActiveInputDevice(options: {
  current: InputDevice;
  padConnected: boolean;
  padBecameActive: boolean;
  keyboardActive: boolean;
  touchBecameActive?: boolean;
}): InputDevice {
  if (options.touchBecameActive) return "touch";
  if (options.current === "touch" && !options.padBecameActive && !options.keyboardActive) {
    return "touch";
  }
  if (!options.padConnected) return "keyboard";
  if (options.padBecameActive) return "gamepad";
  if (options.keyboardActive) return "keyboard";
  return options.current;
}

export function continuePrompt(device: InputDevice): string {
  if (device === "gamepad") return "PRESS START TO CONTINUE";
  if (device === "touch") return "TAP HIT TO CONTINUE";
  return "PRESS ENTER TO CONTINUE";
}

export function resultsBrowsePrompt(device: InputDevice, view: "podium" | "list"): string {
  const other = view === "podium" ? "RANKINGS" : "PODIUM";
  if (device === "gamepad") return `DPAD LOOK    Y ${other}    START EXIT`;
  if (device === "touch") return `STICK LOOK    HIT ${other}    JUMP EXIT`;
  return `UP DOWN LOOK    LEFT RIGHT ${other}    ENTER EXIT`;
}

export function attackButtonPrompt(device: InputDevice): string {
  if (device === "gamepad") return "Y";
  if (device === "touch") return "HIT";
  return "J";
}

export function markTouchInputActive(): void {
  setActiveInputDevice("touch");
}

export function paintControlHints(root: ParentNode, device: InputDevice): void {
  for (const bar of root.querySelectorAll<HTMLElement>("[data-control-hints]")) {
    bar.dataset.inputDevice = device;
    for (const group of bar.querySelectorAll<HTMLElement>("[data-input-device]")) {
      group.hidden = group.dataset.inputDevice !== device;
    }
  }
}

export function bindControlHints(root: ParentNode = document, view: Window = window): () => void {
  const paint = (device: InputDevice): void => paintControlHints(root, device);
  const unsubscribe = subscribeActiveInputDevice(paint);

  let padWasActive = false;
  let frame = 0;

  const apply = (keyboardActive: boolean): void => {
    const pad = connectedGamepad();
    const padActive = gamepadHasActivity(pad);
    const next = resolveActiveInputDevice({
      current: currentDevice,
      padConnected: Boolean(pad),
      padBecameActive: padActive && !padWasActive,
      keyboardActive
    });
    padWasActive = padActive;
    setActiveInputDevice(next);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    apply(true);
  };

  const poll = (): void => {
    apply(false);
    frame = view.requestAnimationFrame(poll);
  };

  view.addEventListener("keydown", onKeyDown);
  frame = view.requestAnimationFrame(poll);

  return () => {
    unsubscribe();
    view.removeEventListener("keydown", onKeyDown);
    view.cancelAnimationFrame(frame);
  };
}

function setActiveInputDevice(next: InputDevice): void {
  if (next === currentDevice) return;
  currentDevice = next;
  for (const listener of listeners) listener(currentDevice);
}
